import type { ReactNode } from 'react';

import { AuthProvider } from '@/auth/AuthContext';
import { SessionAvatarSync } from '@/features/candidate-profile/components/SessionAvatarSync';
import { WorkspaceProvider } from '@/features/company-portal/workspace/WorkspaceProvider';

import { ApolloAppProvider } from './ApolloAppProvider';
import { AvatarProvider } from './AvatarProvider';
import { DateLocalizationProvider } from './DateLocalizationProvider';
import { SnackbarAppProvider } from './SnackbarAppProvider';
import { ThemeModeProvider } from './ThemeModeProvider';

/**
 * The composition root for every app-wide context provider (docs/development/FrontendGuidelines.md's
 * `providers/` folder). Mount once in `app/App.tsx`, inside the router (none of these need router
 * context today, but route-aware providers added later should nest here too, not beside it).
 */
export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <ThemeModeProvider>
      <DateLocalizationProvider>
        <SnackbarAppProvider>
          <ApolloAppProvider>
            <AuthProvider>
              {/* Workspace membership state (Company module Stage 2) — inside AuthProvider so
                  its queries can skip while signed out, and global so TopNav's workspace
                  switcher and RootRedirect share one cached result. */}
              <WorkspaceProvider>
                <AvatarProvider>
                  {/* Populates the avatar state for the whole session, so the TopNav avatar and the
                      account profile page show the uploaded photo no matter which route was entered
                      first — they cannot query a feature entity themselves. Renders nothing. */}
                  <SessionAvatarSync />
                  {children}
                </AvatarProvider>
              </WorkspaceProvider>
            </AuthProvider>
          </ApolloAppProvider>
        </SnackbarAppProvider>
      </DateLocalizationProvider>
    </ThemeModeProvider>
  );
}
